import { ChangeEvent, useState } from "react";

interface Props {
    modalTitle: string;
    modalSubTitle: string;
    onConfirm: () => void;
    onCancel: () => void;
    onDeny?: (reason: string) => void;
    isDeny?: boolean;
    isLoading?: boolean;
    notes?: string; 
    onNotesChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    notesTitle?: string;
    notesPlaceholder?: string;
    notesInstruction?: string;
}

export default function ConfirmDefaultModal({
    modalTitle,
    modalSubTitle,
    onCancel,
    onConfirm,
    onDeny,
    isDeny = false,
    isLoading = false,
    notes = "",
    onNotesChange,
    notesTitle = "Reason for denial",
    notesPlaceholder = "Enter the reason for denying this submission...",
    notesInstruction = "Please provide a clear reason for the requester.",
}: Props) {

    const handleDeny = () => {
        const reason = notes.trim();
        if (!reason || isLoading) return;
        onDeny?.(reason);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]">
            <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                <div className="flex flex-col p-5 sm:p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-full ${isDeny ? "bg-red-50 text-red-500" : "bg-[#186cb2]/10 text-[#186cb2]"}`}>
                            {isDeny ? (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6 6 18" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.84 2.73 17a2 2 0 0 0 1.73 3h15.08a2 2 0 0 0 1.73-3L13.7 3.84a2 2 0 0 0-3.4 0z" />
                                </svg>
                            )}
                        </div>
                        <div className="mt-3">
                            <h2 className="text-sm font-semibold text-gray-900">
                                {modalTitle}
                            </h2>
                            <p className="mx-auto mt-1.5 text-xs leading-relaxed text-gray-500">
                                {modalSubTitle}
                            </p>
                        </div>
                    </div>
                    {isDeny && (
                        <div className="mt-4">
                            <label className="mb-1.5 block text-left text-xs font-semibold text-gray-700">
                                {notesTitle}
                                <span className="ml-1 text-red-500">*</span>
                            </label>
                            <textarea
                                value={notes}
                                onChange={onNotesChange}
                                disabled={isLoading}
                                rows={4}
                                placeholder={notesPlaceholder}
                                className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-red-300 focus:ring-2 focus:ring-red-100 disabled:cursor-not-allowed disabled:bg-gray-50"
                            />
                            <p className="mt-1 text-left text-[10px] text-gray-400">
                                {notesInstruction}
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/70 p-3 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        disabled={isLoading}
                        onClick={onCancel}
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:min-w-[110px]"
                    >
                        Back
                    </button>

                    {isDeny ? (
                        <button
                            type="button"
                            onClick={handleDeny}
                            disabled={isLoading || !notes.trim()}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-red-500/20 transition hover:bg-red-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[110px]"
                        >
                            {isLoading ? (
                                <>
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Denying...
                                </>
                            ) : (
                                "Yes, Deny"
                            )}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isLoading}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-[#186cb2]/20 transition hover:bg-[#155d9b] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:min-w-[110px]"
                        >
                            {isLoading ? (
                                <>
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Confirming...
                                </>
                            ) : (
                                "Yes, Confirm"
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}